import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useClients } from '../hooks/useClients';
import { useForm } from 'react-hook-form';
import UserModal from '../components/users/UserModal';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Pagination from '../components/ui/Pagination';
import ClientSiteCell from '../components/clients/ClientSiteCell';
import SubscriptionBadge from '../components/clients/SubscriptionBadge';
import PresenceDot from '../components/ui/PresenceDot';

// URL du portail (mode assistance) — même pattern que les demandes de création
const PORTAL_URL = process.env.REACT_APP_PORTAL_URL || 'https://portail.monsitedirect.fr';

// Filtres rapides de la liste Clients
const FILTERS = [
  { id: 'all', label: 'Tous' },
  { id: 'site_en_ligne', label: 'Site en ligne' },
  { id: 'non_publie', label: 'Non publié' },
  { id: 'creation_assistee', label: 'En création assistée' },
  { id: 'entreprise', label: 'Entreprises' },
  { id: 'abonnement_actif', label: 'Abonnement actif' }
];

// Libellés des statuts de création assistée (cohérents avec creationRequestService)
const REQUEST_STATUS_LABELS = {
  brouillon: 'Brouillon',
  nouvelle: 'Demande reçue',
  creation_en_cours: 'Création en cours',
  verification: 'Vérification',
  site_pret: 'Site prêt',
  email_envoye: 'Livré'
};

const getClientUid = (client) => client.uid || client.id;

const getClientName = (client) =>
  `${client.firstName || ''} ${client.lastName || ''}`.trim() || client.email || 'Client sans nom';

const getInitials = (client) => {
  const name = getClientName(client).replace(/[^a-zA-ZÀ-ÿ\s]/g, '').trim();
  const parts = name.split(/\s+/);
  return ((parts[0]?.[0] || '?') + (parts[1]?.[0] || '')).toUpperCase();
};

/** Pastille « création assistée en cours » affichée sous le nom du client */
const renderAssistanceBadge = (client) => {
  const request = client.request;
  if (!request || !request.hasRequest || request.trackingClosed) return null;
  const label = REQUEST_STATUS_LABELS[request.status] || request.status;
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium bg-accent/15 text-accent border border-accent/25 mt-1">
      ✦ Création assistée — {label}
    </span>
  );
};

// Chemins SVG compacts pour les actions de ligne
const PATHS = {
  assist: 'M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z',
  visit: 'M13.5 6H5.25A2.25 2.25 0 003 8.25v10.5A2.25 2.25 0 005.25 21h10.5A2.25 2.25 0 0018 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25',
  edit: 'M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21h-9.5A2.25 2.25 0 014 18.75V8.25A2.25 2.25 0 016.25 6H11',
  trash: 'M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0',
  search: 'M21 21l-5.197-5.197m0 0A7.5 7.5 0 105.196 5.196a7.5 7.5 0 0010.607 10.607z',
  xmark: 'M6 18L18 6M6 6l12 12',
  folder: 'M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z'
};

const RowIcon = ({ d, className = 'w-[18px] h-[18px]' }) => (
  <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className={className}>
    <path strokeLinecap="round" strokeLinejoin="round" d={d} />
  </svg>
);

const ClientsPage = () => {
  // État local : pagination, recherche, filtre, modales
  const [page, setPage] = useState(1);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState(null);

  const {
    clients,
    isLoading,
    isError,
    error,
    totalUsers,
    isDataset,
    datasetTotal,
    updateUser,
    deleteUser,
    isDeleting
  } = useClients(10, page, 'createdAt', isSearching ? searchTerm : '', filter);

  const { register, handleSubmit, reset } = useForm({
    defaultValues: { searchQuery: '' }
  });

  // Total utilisé pour la pagination : jeu filtré (mode dataset) ou total des users
  const visibleTotal = isDataset && datasetTotal !== null ? datasetTotal : totalUsers;
  const totalPages = Math.max(1, Math.ceil(visibleTotal / 10));

  const onSearch = (data) => {
    setSearchTerm(data.searchQuery);
    setIsSearching(!!data.searchQuery);
    setPage(1);
  };

  const clearSearch = () => {
    reset({ searchQuery: '' });
    setSearchTerm('');
    setIsSearching(false);
  };

  const applyFilter = (nextFilter) => {
    setFilter(nextFilter);
    setPage(1);
  };

  // ---- Modales / actions ----
  const openEditModal = (client) => {
    setCurrentUser(client);
    setIsUserModalOpen(true);
  };

  const closeUserModal = () => {
    setIsUserModalOpen(false);
    setCurrentUser(null);
  };

  const handleSaveUser = (userData) => {
    if (currentUser) {
      updateUser({ userId: currentUser.id, userData });
    }
    closeUserModal();
  };

  const openDeleteConfirm = (client) => {
    setClientToDelete(client);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteConfirm = () => {
    setIsDeleteModalOpen(false);
    setClientToDelete(null);
  };

  const confirmDeleteClient = () => {
    if (clientToDelete) {
      deleteUser(clientToDelete.id);
      closeDeleteConfirm();
    }
  };

  // ---- Boutons d'action par ligne ----
  const getClientActions = (client) => {
    const uid = getClientUid(client);
    const assistUrl = `${PORTAL_URL}/admin/assist?uid=${encodeURIComponent(uid)}`;
    const site = client.site;
    const iconBtn = 'p-1.5 transition-colors rounded-lg hover:bg-base-200';

    return (
      <div className="flex gap-1 items-center justify-end">
        <Link
          to={`/clients/${uid}`}
          className={`${iconBtn} text-primary hover:text-primary-focus`}
          title="Voir le dossier client"
        >
          <RowIcon d={PATHS.folder} />
        </Link>
        <a
          href={assistUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={`${iconBtn} text-accent hover:text-accent-focus`}
          title="Mode assistance (ouvrir le portail en tant que ce client)"
        >
          <RowIcon d={PATHS.assist} />
        </a>
        {site && site.domainName ? (
          <a
            href={`https://${site.domainName}`}
            target="_blank"
            rel="noopener noreferrer"
            className={`${iconBtn} text-success hover:text-success-focus`}
            title="Voir le site en ligne"
          >
            <RowIcon d={PATHS.visit} />
          </a>
        ) : null}
        <button
          onClick={() => openEditModal(client)}
          className={`${iconBtn} text-info hover:text-info-focus`}
          title="Éditer"
        >
          <RowIcon d={PATHS.edit} />
        </button>
        <button
          onClick={() => openDeleteConfirm(client)}
          className={`${iconBtn} text-error hover:text-error-focus`}
          title="Supprimer"
        >
          <RowIcon d={PATHS.trash} />
        </button>
      </div>
    );
  };

  // ---- Rendu d'une ligne « client » (desktop) ----
  const renderClientRow = (client) => (
    <tr key={client.id} className="border-t border-base-200 hover:bg-base-200/50 align-top">
      <td className="px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 h-9 w-9 rounded-full bg-primary/15 text-primary flex items-center justify-center text-xs font-bold">
            {getInitials(client)}
          </div>
          <div className="min-w-0">
            <Link
              to={`/clients/${getClientUid(client)}`}
              className="font-medium text-base-content hover:text-primary hover:underline truncate block max-w-full"
              title="Voir le dossier client"
            >
              {getClientName(client)}
            </Link>
            <p className="text-xs text-base-content/60 truncate">{client.email}</p>
            {client.isCompany && client.companyName && (
              <p className="text-xs text-base-content/50 truncate mt-0.5">Entreprise · {client.companyName}</p>
            )}
            {renderAssistanceBadge(client)}
          </div>
        </div>
      </td>
      <td className="px-4 py-3 align-top whitespace-nowrap">
        <PresenceDot uid={getClientUid(client)} />
      </td>
      <td className="px-4 py-3 whitespace-nowrap">
        <SubscriptionBadge plan={client.subscriptionPlan} subscriptionStatus={client.subscriptionStatus} />
      </td>
      <td className="px-4 py-3"><ClientSiteCell client={client} /></td>
      <td className="px-4 py-3">{getClientActions(client)}</td>
    </tr>
  );

  // ---- Rendu mobile (cartes) ----
  const renderMobileClientCard = (client) => (
    <div key={client.id} className="bg-base-100 rounded-lg shadow-sm p-4 mb-4 border-l-4 border-primary">
      <div className="flex justify-between items-start gap-2">
        <div className="min-w-0">
          <span className="flex items-center gap-1.5">
            <Link
              to={`/clients/${getClientUid(client)}`}
              className="font-medium text-base-content hover:text-primary hover:underline"
              title="Voir le dossier client"
            >
              {getClientName(client)}
            </Link>
            <PresenceDot uid={getClientUid(client)} />
          </span>
          <p className="text-sm text-base-content/80 truncate">{client.email}</p>
          {client.isCompany && client.companyName && (
            <p className="text-xs text-base-content/60">Entreprise · {client.companyName}</p>
          )}
        </div>
        {getClientActions(client)}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2">
        <SubscriptionBadge plan={client.subscriptionPlan} subscriptionStatus={client.subscriptionStatus} />
        {renderAssistanceBadge(client)}
      </div>
      <div className="mt-3">
        <ClientSiteCell client={client} />
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto p-4">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-primary mb-2">Clients</h1>
        <div className="text-sm breadcrumbs text-base-content/60">
          <ul><li>MSD Admin</li><li>Clients</li></ul>
        </div>
      </div>

      <div className="bg-base-100 rounded-lg shadow-sm mb-6">
        <div className="p-4 md:p-6 border-b border-base-200">
          <div className="flex flex-col md:flex-row justify-between md:items-center mb-4 gap-4">
            <div>
              <h2 className="text-xl font-bold text-base-content">Liste des clients</h2>
              <p className="text-sm text-base-content/60 mt-1">
                {isLoading ? 'Chargement…' : `${visibleTotal} client(s)`}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
              <form onSubmit={handleSubmit(onSearch)} className="flex w-full sm:w-auto">
                <div className="relative flex-grow">
                  <input {...register('searchQuery')} type="text"
                    placeholder="Email, nom, entreprise, domaine…"
                    className="input input-bordered rounded-r-none w-full" />
                  {isSearching && (
                    <button type="button" onClick={clearSearch}
                      className="absolute inset-y-0 right-0 flex items-center px-3 text-base-content/50 hover:text-base-content/80"
                      title="Effacer la recherche">
                      <RowIcon d={PATHS.xmark} className="w-4 h-4" />
                    </button>
                  )}
                </div>
                <button type="submit" className="btn btn-square btn-outline btn-primary rounded-l-none"
                  title="Rechercher">
                  <RowIcon d={PATHS.search} className="w-5 h-5" />
                </button>
              </form>
            </div>
          </div>

          <div className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button key={f.id} onClick={() => applyFilter(f.id)}
                className={`px-3 py-1.5 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  filter === f.id
                    ? 'bg-primary text-primary-content'
                    : 'bg-base-200 hover:bg-base-300 text-base-content'
                }`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
        <div className="p-0 md:p-4">
          {isLoading ? (
            <div className="text-center py-16">
              <div className="inline-block animate-spin w-10 h-10 border-4 border-primary border-t-transparent rounded-full"></div>
              <p className="mt-4 text-base-content/70">Chargement des clients...</p>
            </div>
          ) : isError ? (
            <div className="text-center py-16">
              <p className="text-error font-medium">Erreur lors du chargement des clients.</p>
              <p className="text-sm mt-2 text-base-content/70">{error?.message || 'Veuillez réessayer plus tard.'}</p>
            </div>
          ) : clients.length === 0 ? (
            <div className="text-center py-16">
              <p className="text-base-content font-medium">
                {isSearching || filter !== 'all'
                  ? 'Aucun client ne correspond à votre recherche ou à ce filtre.'
                  : 'Aucun client dans la base de données.'}
              </p>
              {(isSearching || filter !== 'all') && (
                <button onClick={() => { clearSearch(); applyFilter('all'); }}
                  className="btn btn-outline btn-sm mt-4">
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          ) : (
            <>
              <div className="md:hidden">{clients.map((c) => renderMobileClientCard(c))}</div>
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-base-200/50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">Client</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">En ligne</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">Abonnement</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-base-content/70 uppercase tracking-wider">Site web</th>
                      <th className="px-4 py-3 text-right text-xs font-medium text-base-content/70 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody>{clients.map((c) => renderClientRow(c))}</tbody>
                </table>
              </div>
            </>
          )}
        </div>
        {!isLoading && clients.length > 0 && (
          <div className="p-4 border-t border-base-200 flex justify-center">
            <Pagination currentPage={page} totalPages={totalPages} onPageChange={setPage} />
          </div>
        )}
      </div>

      {/* Modale création/édition */}
      <UserModal isOpen={isUserModalOpen} onClose={closeUserModal} onSave={handleSaveUser} user={currentUser} />

      {/* Confirmation de suppression */}
      <ConfirmDialog
        isOpen={isDeleteModalOpen}
        title="Supprimer le client"
        message={`Supprimer définitivement ${getClientName(clientToDelete || {})} (${clientToDelete?.email}) ?\nCette action supprime aussi le compte de connexion, le site en ligne et toutes les données associées. Irréversible.`}
        confirmLabel="Supprimer"
        cancelLabel="Annuler"
        confirmColor="error"
        isProcessing={isDeleting}
        onConfirm={confirmDeleteClient}
        onCancel={closeDeleteConfirm}
      />
    </div>
  );
};

export default ClientsPage;






